import React from 'react';
import { connect } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import { translate } from 'react-i18next';
import moment from 'moment';
import { Paginator } from 'primereact/paginator';
import { MultiSelect } from 'primereact/multiselect';
import CBMediaQuery from '../shared/CBMediaQuery';
import CBDatePickerInput from '../shared/CBDatePickerInput';
import CBSelectFilters from '../shared/CBSelectFilters';
import VehicleListTh from '../OrganizationGarageView/OrganizationGarageComponents/VehicleListTh';
import VehicleListTd from '../OrganizationGarageView/OrganizationGarageComponents/VehicleListTd';
import VehicleMessageHistoryModal from '../MessagesView/MessagesViewComponents/VehicleMessageHistoryModal';
import CBMobileCard from '../shared/CBMobileCard';
import CBStatRow from '../shared/CBStatRow';
import CBMobileSortSelect from '../shared/CBMobileSortSelect';
import { API_URL } from '../../config';
import { finnishDateFormat, getStyle, formatTaskHoursAndAmount } from '../../utils/utils';
import {
  fetchChainPartsAnalysis as _fetchChainPartsAnalysis,
  fetchChainPartsAnalysisSummary as _fetchChainPartsAnalysisSummary,
  fetchChainCareServices as _fetchChainCareServices,
  fetchChainMemberships as _fetchChainMemberships,
  fetchChainMaintenanceDetails as _fetchChainMaintenanceDetails,
} from '../../actions/chainActions';
import { companySwitchState, listStateKey, loadListState, saveListState } from '../../utils/listState';

var SELECTED_FIELDS_KEY = 'selectedChainPartsUsageFields';


function getPercentageColor(pct) {
  if (pct >= 80) return '#4CAF50';
  if (pct >= 50) return '#FF9800';
  return '#F44336';
}

function formatPrice(value) {
  if (value === null || value === undefined) return '-';
  return Number(value).toLocaleString('fi-FI', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' \u20AC';
}

function renderPartRow(part, isChainPart, tooltipText, showPrices) {
  var shouldShowPrices = showPrices !== false;
  return (
    <div key={part.id} style={partRowStyles.container}>
      <div style={partRowStyles.leftSection}>
        {isChainPart !== null && isChainPart ? (
          <span style={partRowStyles.chainBadge} title={tooltipText}>{'\u2713'}</span>
        ) : isChainPart !== null ? (
          <span style={partRowStyles.otherBadge} title={tooltipText}>{'\u2717'}</span>
        ) : null}
        <span style={partRowStyles.name}>{part.name}</span>
        {part.amount != null && (
          <span style={partRowStyles.amount}>
            {part.amount} {part.unit || 'kpl'}
          </span>
        )}
      </div>
      <div style={partRowStyles.rightSection}>
        {isChainPart && part.type_code && (
          <span style={partRowStyles.typeCode}>{part.type_code}</span>
        )}
        {shouldShowPrices && (
          <span style={partRowStyles.price}>
            {part.total_price != null
              ? formatPrice(part.total_price)
              : '-'}
          </span>
        )}
      </div>
    </div>
  );
}

function renderTaskRow(task, t, showPrices) {
  var shouldShowPrices = showPrices !== false;
  return (
    <div key={task.id} style={partRowStyles.container}>
      <div style={partRowStyles.leftSection}>
        <span style={partRowStyles.name}>{task.name}</span>
      </div>
      <div style={partRowStyles.rightSection}>
        <span style={partRowStyles.amount}>
          {formatTaskHoursAndAmount(task, t('hoursUnit'), t('pcsLabel'))}
        </span>
        {shouldShowPrices && (
          <span style={partRowStyles.price}>
            {task.total_price != null
              ? formatPrice(task.total_price)
              : '-'}
          </span>
        )}
      </div>
    </div>
  );
}


class ChainPartsUsageView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      ordering: '-maintenance_date',
      filters: {},
      page: 1,
      expandedRowId: null,
      fields: [],
      selectedFields: [],
      historyModalOpen: false,
      historyReg: null,
      historyDate: null,
    };
    this.tableScrollRef = React.createRef();
    this.topScrollRef = React.createRef();
    this._isSyncingScroll = false;
  }

  componentDidMount() {
    this.syncTopScrollWidth();
    if (typeof ResizeObserver !== 'undefined' && this.tableScrollRef.current) {
      this._resizeObserver = new ResizeObserver(this.syncTopScrollWidth);
      this._resizeObserver.observe(this.tableScrollRef.current);
    }
  }

  componentWillUnmount() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
  }

  syncTopScrollWidth = () => {
    if (this.tableScrollRef.current && this.topScrollRef.current) {
      var inner = this.topScrollRef.current.firstChild;
      if (inner) {
        inner.style.width = this.tableScrollRef.current.scrollWidth + 'px';
      }
    }
  };

  handleTopScroll = () => {
    if (this._isSyncingScroll) return;
    this._isSyncingScroll = true;
    if (this.tableScrollRef.current && this.topScrollRef.current) {
      this.tableScrollRef.current.scrollLeft = this.topScrollRef.current.scrollLeft;
    }
    this._isSyncingScroll = false;
  };

  handleTableScroll = () => {
    if (this._isSyncingScroll) return;
    this._isSyncingScroll = true;
    if (this.topScrollRef.current && this.tableScrollRef.current) {
      this.topScrollRef.current.scrollLeft = this.tableScrollRef.current.scrollLeft;
    }
    this._isSyncingScroll = false;
  };

  getChainObj = () => {
    var { chain, user, selectedChainId } = this.props;
    return chain || (user && user.chains && user.chains.find(function(c) { return c.id == selectedChainId; }));
  };

  rebuildFields = () => {
    var { t } = this.props;
    var chainObj = this.getChainObj();

    var isPartsVendor = chainObj && chainObj.chain_type === 'parts_vendor';
    var showPrices = !chainObj || chainObj.show_maintenance_prices !== false;

    var allFields = [
      { label: t('headingMileage'), value: 'mileage' },
      { label: t('headingWorkshop'), value: 'workshop' },
      { label: t('headingCustomer'), value: 'customer' },
      { label: t('headingInvoiceNr'), value: 'invoice_number' },
    ];

    if (isPartsVendor && showPrices) {
      allFields.push({ label: t('headingChainParts'), value: 'chain_parts_price' });
      allFields.push({ label: t('headingOtherParts'), value: 'other_parts_price' });
    }
    if (showPrices) {
      allFields.push({ label: t('headingTotalParts'), value: 'total_parts_price' });
      allFields.push({ label: t('headingTasksPrice'), value: 'total_tasks_price' });
    }
    if (!isPartsVendor) {
      allFields.push({ label: t('headingTasks'), value: 'total_tasks_count' });
      allFields.push({ label: t('headingBilledHours'), value: 'total_billed_hours' });
    }
    allFields.push({ label: t('headingApprovalStatus'), value: 'approval_status' });

    var validValues = allFields.map(function (f) { return f.value; });
    var currentSelected = this.state.selectedFields || [];
    var filteredSelected = currentSelected.filter(function (v) { return validValues.indexOf(v) !== -1; });

    if (filteredSelected.length === 0) {
      filteredSelected = this.getDefaultFields().filter(function (v) { return validValues.indexOf(v) !== -1; });
    }

    this.setState({
      fields: allFields,
      selectedFields: filteredSelected,
    });
  }

  componentWillMount() {
    const { selectedChainId } = this.props;

    var savedFields = null;
    try {
      savedFields = JSON.parse(localStorage.getItem(SELECTED_FIELDS_KEY));
    } catch (e) { /* ignore */ }

    this.setState({
      selectedFields: savedFields || this.getDefaultFields(),
    }, () => {
      this.rebuildFields();
    });

    if (selectedChainId) {
      this.props.fetchChainMemberships(selectedChainId);
      this.props.fetchChainCareServices(selectedChainId);
      const savedState = this.loadListStateFromStorage();
      const { ordering, filters, page } = companySwitchState(savedState, this.state.ordering);
      this.setState({ ordering, filters, page }, () => {
        this.refetchAnalysis();
      });
    }
  }

  componentDidUpdate(prevProps) {
    this.syncTopScrollWidth();
    const { selectedChainId, chain } = this.props;

    if (selectedChainId !== prevProps.selectedChainId && selectedChainId) {
      this.props.fetchChainMemberships(selectedChainId);
      this.props.fetchChainCareServices(selectedChainId);
      this.rebuildFields();
      // A chain with no saved state of its own starts clean: keeping the
      // previous chain's filters would both show the wrong list and — since
      // refetchAnalysis persists what it sent — write them into the new chain's
      // entry (see utils/listState).
      const savedState = this.loadListStateFromStorage();
      const { ordering, filters, page } = companySwitchState(savedState, this.state.ordering);
      this.setState({ ordering, filters, page }, () => {
        this.refetchAnalysis();
      });
    }

    var chainObj = this.getChainObj();
    var prevChainObj = prevProps.chain || (prevProps.user && prevProps.user.chains && prevProps.user.chains.find(function(c) { return c.id == prevProps.selectedChainId; }));
    if (chainObj && (!prevChainObj || prevChainObj.chain_type !== chainObj.chain_type || prevChainObj.show_maintenance_prices !== chainObj.show_maintenance_prices)) {
      this.rebuildFields();
    }
  }

  getDefaultFields = () => {
    return [
      'mileage',
      'workshop',
      'customer',
      'invoice_number',
      'chain_parts_price',
      'other_parts_price',
      'total_parts_price',
    ];
  }

  setSelectedFields = (newSelectedFields) => {
    this.setState({ selectedFields: newSelectedFields });
    try {
      localStorage.setItem(SELECTED_FIELDS_KEY, JSON.stringify(newSelectedFields));
    } catch (e) { /* ignore */ }
  }

  getVisibleColumnCount = () => {
    var { selectedFields } = this.state;
    var chainObj = this.getChainObj();
    var isPartsVendor = chainObj && chainObj.chain_type === 'parts_vendor';
    // date + reg nr + message column + settings column = 4
    // + percentage column (only for parts vendor) = +1
    // plus each toggled field
    var base = isPartsVendor ? 5 : 4;
    return base + selectedFields.length;
  }

  getCompanyScopeKey = () => {
    const { selectedChainId } = this.props;
    return listStateKey('chainPartsUsageListState', 'chain', selectedChainId);
  }

  saveListStateToStorage = () => {
    const { ordering, filters, page } = this.state;
    saveListState(this.getCompanyScopeKey(), { ordering, filters, page });
  }

  loadListStateFromStorage = () => loadListState(this.getCompanyScopeKey())

  refetchAnalysis = () => {
    const { selectedChainId } = this.props;
    const { ordering, filters, page } = this.state;

    if (!selectedChainId) return;

    this.setState({ expandedRowId: null });

    const params = { ...filters, ordering, page };

    this.props.fetchChainPartsAnalysis(selectedChainId, params);
    this.props.fetchChainPartsAnalysisSummary(selectedChainId, filters);
    this.saveListStateToStorage();
  };

  changeOrdering = (clickedOrdering) => {
    const { ordering } = this.state;
    let newOrdering;
    if (ordering === clickedOrdering) {
      newOrdering = '-' + ordering;
    } else if ('-' + ordering === clickedOrdering) {
      newOrdering = '-maintenance_date';
    } else {
      newOrdering = clickedOrdering;
    }
    this.setState({
      ordering: newOrdering,
      page: 1,
    }, () => {
      this.saveListStateToStorage();
      this.refetchAnalysis();
    });
  };

  getOrderingSymbol = (columnName) => {
    const { ordering } = this.state;
    if (ordering === columnName) {
      return '/img/down_arrow_icon.svg';
    } else if (ordering === '-' + columnName) {
      return '/img/up_arrow_icon.svg';
    }
    return undefined;
  };

  changePage = (event) => {
    const { chainPartsAnalysisPagination } = this.props;
    const pageSize = chainPartsAnalysisPagination && chainPartsAnalysisPagination.page_size ? chainPartsAnalysisPagination.page_size : 20;
    const newPage = Math.floor(event.first / pageSize) + 1;
    this.setState({ page: newPage }, () => {
      this.saveListStateToStorage();
      this.refetchAnalysis();
    });
  };

  openMessageHistory = (analysis) => {
    this.setState({
      historyModalOpen: true,
      historyReg: analysis.registration_number,
      historyDate: analysis.maintenance_date,
    });
  };

  closeMessageHistory = () => {
    this.setState({ historyModalOpen: false });
  };

  // The envelope is dimmed on rows with nothing in the history window, so the
  // eye goes to the rows that have something to read. The count comes from the
  // backend (message_history_count); undefined/null means "not known".
  renderMessageHistoryButton = (analysis) => {
    const { t } = this.props;
    var count = analysis.message_history_count;
    var hasNone = count === 0;
    var title = t('viewMessageHistory');
    if (hasNone) {
      title = t('viewMessageHistoryNone');
    } else if (count > 0) {
      title = t('viewMessageHistoryCount', { count: count });
    }
    var buttonStyle = hasNone
      ? Object.assign({}, styles.default.messageHistoryButton, styles.default.messageHistoryButtonEmpty)
      : styles.default.messageHistoryButton;
    return (
      <div
        onClick={(e) => { e.stopPropagation(); this.openMessageHistory(analysis); }}
        title={title}
        className='opacity-on-hover'
        style={buttonStyle}
      >
        <img
          src='/img/icon_message_solid.svg'
          alt=''
          style={{ width: 20, height: 16, verticalAlign: 'middle', opacity: hasNone ? 0.3 : 1 }}
        />
        {count > 0 && (
          <span style={styles.default.messageHistoryCount}>{count}</span>
        )}
      </div>
    );
  };

  toggleRow = (analysis) => {
    const { expandedRowId } = this.state;

    // Collapse if clicking the already-expanded row
    if (expandedRowId === analysis.id) {
      this.setState({ expandedRowId: null });
      return;
    }

    // Expand the new row
    this.setState({ expandedRowId: analysis.id });
    this.ensureDetailsLoaded(analysis);
  };

  // Lazily fetch the parts/tasks details for a maintenance. Shared by the desktop
  // expandable row (toggleRow) and the mobile card's expand toggle (onExpand).
  ensureDetailsLoaded = (analysis) => {
    const { selectedChainId, chainMaintenanceDetails } = this.props;
    var cached = chainMaintenanceDetails && chainMaintenanceDetails[analysis.maintenance];
    if (cached && (cached.data || cached.loading)) {
      return;
    }
    if (selectedChainId && analysis.maintenance) {
      this.props.fetchChainMaintenanceDetails(analysis.maintenance, selectedChainId);
    }
  };

  renderExpandedParts = (maintenanceId, stacked) => {
    const { t, chainMaintenanceDetails } = this.props;
    var cached = chainMaintenanceDetails && chainMaintenanceDetails[maintenanceId];
    var chainObj = this.getChainObj();
    var isPartsVendor = chainObj && chainObj.chain_type === 'parts_vendor';
    var showPrices = !chainObj || chainObj.show_maintenance_prices !== false;

    if (!cached || cached.loading) {
      return (
        <div style={expandedStyles.loading}>
          <p>{t('loadingParts')}</p>
        </div>
      );
    }

    if (cached.error) {
      return (
        <div style={expandedStyles.loading}>
          <p>{t('errorLoadingParts')}</p>
        </div>
      );
    }

    var parts = cached.data && cached.data.parts ? cached.data.parts : [];
    var tasks = cached.data && cached.data.tasks ? cached.data.tasks : [];

    // On mobile the two sections stack vertically instead of sitting side-by-side.
    var contentStyle = stacked
      ? Object.assign({}, expandedStyles.content, { flexDirection: 'column', gap: 16, maxWidth: '100%', padding: 0 })
      : expandedStyles.content;

    if (isPartsVendor) {
      var chainParts = parts.filter(function (p) { return p.is_chain_part === true; });
      var otherParts = parts.filter(function (p) { return p.is_chain_part !== true; });

      return (
        <div style={contentStyle}>
          <div style={expandedStyles.section}>
            <p style={expandedStyles.sectionTitle}>
              {t('chainPartsSection')} ({chainParts.length})
            </p>
            {chainParts.length > 0 ? (
              chainParts.map(function (part) {
                return renderPartRow(part, true, t('chainPartTooltip'), showPrices);
              })
            ) : (
              <p style={expandedStyles.noParts}>-</p>
            )}
            <p style={Object.assign({}, expandedStyles.sectionTitle, expandedStyles.subBlock)}>
              {t('otherPartsSection')} ({otherParts.length})
            </p>
            {otherParts.length > 0 ? (
              otherParts.map(function (part) {
                return renderPartRow(part, false, t('otherPartTooltip'), showPrices);
              })
            ) : (
              <p style={expandedStyles.noParts}>-</p>
            )}
          </div>
          <div style={expandedStyles.section}>
            <p style={expandedStyles.sectionTitle}>
              {t('tasksSection')} ({tasks.length})
            </p>
            {tasks.length > 0 ? (
              tasks.map(function (task) {
                return renderTaskRow(task, t, showPrices);
              })
            ) : (
              <p style={expandedStyles.noParts}>-</p>
            )}
          </div>
        </div>
      );
    }

    return (
      <div style={contentStyle}>
        <div style={expandedStyles.section}>
          <p style={expandedStyles.sectionTitle}>
            {t('partsSection')} ({parts.length})
          </p>
          {parts.length > 0 ? (
            parts.map(function (part) {
              return renderPartRow(part, null, '', showPrices);
            })
          ) : (
            <p style={expandedStyles.noParts}>-</p>
          )}
        </div>
        {tasks.length > 0 && (
          <div style={expandedStyles.section}>
            <p style={expandedStyles.sectionTitle}>
              {t('tasksSection')} ({tasks.length})
            </p>
            {tasks.map(function (task) {
              return renderTaskRow(task, t, showPrices);
            })}
          </div>
        )}
      </div>
    );
  };

  getWorkshopOptions = () => {
    const { chainMemberships, t } = this.props;
    const options = [
      { value: '', label: t('filterAllWorkshops') },
    ];

    if (chainMemberships && chainMemberships.length > 0) {
      chainMemberships.forEach((membership) => {
        if (membership.workshop && membership.workshop.id && membership.has_chain_care_service_deal) {
          options.push({
            value: String(membership.workshop.id),
            label: membership.workshop.name,
          });
        }
      });
    }

    return options;
  }

  getOrganizationOptions = () => {
    const { chainCareServices, t } = this.props;
    const options = [
      { value: '', label: t('filterAllCustomers') },
    ];
    const seen = {};

    if (chainCareServices && chainCareServices.length > 0) {
      chainCareServices.forEach((cs) => {
        const orgId = cs.organization;
        if (orgId && !seen[orgId]) {
          seen[orgId] = true;
          options.push({
            value: String(orgId),
            label: cs.organization_name || '-',
          });
        }
      });
    }

    return options;
  }

  getPercentageOptions = () => {
    const { t } = this.props;
    return [
      { value: '', label: t('filterAllPercentages') },
      { value: 'below50', label: t('filterBelow50') },
      { value: '50to80', label: t('filter50to80') },
      { value: 'above80', label: t('filterAbove80') },
    ];
  }

  handlePercentageFilter = (changedFilters) => {
    const filters = { ...this.state.filters };
    const value = changedFilters.percentage_range;

    // Remove old percentage filters
    delete filters.min_chain_percentage;
    delete filters.max_chain_percentage;
    delete filters.percentage_range;

    if (value === 'below50') {
      filters.max_chain_percentage = '50';
      filters.percentage_range = value;
    } else if (value === '50to80') {
      filters.min_chain_percentage = '50';
      filters.max_chain_percentage = '80';
      filters.percentage_range = value;
    } else if (value === 'above80') {
      filters.min_chain_percentage = '80';
      filters.percentage_range = value;
    }

    this.setState({ filters, page: 1 }, () => {
      this.saveListStateToStorage();
      this.refetchAnalysis();
    });
  }

  downloadReport = () => {
    var { selectedChainId } = this.props;
    var { filters } = this.state;
    if (!selectedChainId) return;

    var params = new URLSearchParams();
    params.set('chain_id', selectedChainId);
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    if (filters.workshop_id) params.set('workshop_id', filters.workshop_id);
    if (filters.organization_id) params.set('organization_id', filters.organization_id);
    if (filters.invoice_number) params.set('invoice_number', filters.invoice_number);

    window.open(
      API_URL + '/uiapi/1/chain_parts_analysis/download_report/?' + params.toString(),
      '_blank'
    );
  };

  // Chain approvals (workshop -> ketjujohto) and customer approvals (workshop ->
  // fleet customer, "lupaa kysytty / myönnetty / hylätty") side by side.
  getApprovalLabelSets = () => {
    const { t } = this.props;
    return {
      chain: { pending: t('approvalPending'), approved: t('approvalApproved'), rejected: t('approvalRejected') },
      customer: {
        pending: t('approvalSentToCustomer'),
        approved: t('approvalCustomerApproved'),
        rejected: t('approvalCustomerRejected'),
      },
    };
  };

  getApprovalBadge = (analysis) => {
    var colors = { pending: '#e67e22', approved: '#27ae60', rejected: '#e74c3c' };
    var labelSets = this.getApprovalLabelSets();
    var chainRequests = analysis.approval_requests || [];
    var customerRequests = analysis.customer_approval_requests || [];
    if (!chainRequests.length && !customerRequests.length) {
      return '-';
    }
    var renderRow = function (ar, labels, keyPrefix) {
      var sentAt = ar.created_at ? moment(ar.created_at).format('D.M.') : '';
      return (
        <span
          key={keyPrefix + ar.id}
          title={ar.subject || ''}
          style={{ color: colors[ar.approval_status] || '#fff', fontWeight: 'bold', fontSize: 12 }}
        >
          {labels[ar.approval_status] || ar.approval_status}{sentAt ? ' ' + sentAt : ''}
        </span>
      );
    };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {chainRequests.map(function (ar) { return renderRow(ar, labelSets.chain, 'chain-'); })}
        {customerRequests.map(function (ar) { return renderRow(ar, labelSets.customer, 'customer-'); })}
      </div>
    );
  };

  // Build the summary stat items for the responsive CBStatRow (mobile grid).
  getSummaryItems = (summary, isPartsVendor, showPrices) => {
    const { t } = this.props;
    if (!summary) {
      return [];
    }
    if (!showPrices) {
      return [{ label: t('summaryTotalMaintenances'), value: summary.total_count || 0 }];
    }
    var items = [
      { label: t('summaryTotalMaintenances'), value: summary.total_count || 0 },
    ];
    if (isPartsVendor) {
      items.push({
        label: t('summaryChainPartsPrice'),
        value: summary.chain_parts_price_total != null ? formatPrice(summary.chain_parts_price_total) : '-',
      });
      items.push({
        label: t('summaryOtherPartsPrice'),
        value: summary.other_parts_price_total != null ? formatPrice(summary.other_parts_price_total) : '-',
      });
    }
    items.push({
      label: t('summaryTotalPartsPrice'),
      value: summary.total_parts_price_total != null ? formatPrice(summary.total_parts_price_total) : '-',
    });
    items.push({
      label: t('summaryTotalTasksPrice'),
      value: summary.total_tasks_price_total != null ? formatPrice(summary.total_tasks_price_total) : '-',
    });
    if (isPartsVendor) {
      items.push({
        label: t('summaryAveragePercentage'),
        value: summary.average_chain_percentage != null ? summary.average_chain_percentage.toFixed(1) + ' %' : '-',
        valueColor: getPercentageColor(summary.average_chain_percentage || 0),
      });
    }
    if (!isPartsVendor && summary.total_tasks_count != null) {
      items.push({
        label: t('summaryTotalBilledHours'),
        value: summary.total_billed_hours != null ? Number(summary.total_billed_hours).toFixed(1) + ' h' : '-',
      });
    }
    return items;
  };

  // Hand-picked fields shown on the mobile card face.
  getCardFaceFields = (analysis, isPartsVendor, showPrices, pctColor) => {
    const { t } = this.props;
    var fields = [
      {
        label: t('headingDate'),
        value: analysis.maintenance_date ? moment(analysis.maintenance_date).format('DD.MM.YYYY') : '-',
      },
    ];
    if (analysis.mileage) {
      fields.push({ label: t('headingMileage'), value: Number(analysis.mileage).toLocaleString('fi-FI') + ' km' });
    }
    if (isPartsVendor) {
      fields.push({
        label: t('headingChainPartsPercentage'),
        value: analysis.chain_parts_percentage != null ? Number(analysis.chain_parts_percentage).toFixed(1) + ' %' : '-',
        valueColor: pctColor,
      });
    }
    if (showPrices) {
      fields.push({ label: t('headingTotalParts'), value: formatPrice(analysis.total_parts_price) });
    }
    return fields;
  };

  // The full column set + values for the "show all fields" dialog.
  getCardAllFields = (analysis, isPartsVendor, showPrices) => {
    const { t } = this.props;
    var fields = [
      {
        label: t('headingDate'),
        value: analysis.maintenance_date ? moment(analysis.maintenance_date).format('DD.MM.YYYY') : '-',
      },
      { label: t('headingRegNr'), value: analysis.registration_number || '-' },
      { label: t('headingInvoiceNr'), value: analysis.workshop_invoice_number || '-' },
      { label: t('headingVehicle'), value: [analysis.manufacturer, analysis.model_name].filter(Boolean).join(' ') || '-' },
      { label: t('headingMileage'), value: analysis.mileage ? Number(analysis.mileage).toLocaleString('fi-FI') + ' km' : '-' },
      { label: t('headingWorkshop'), value: analysis.workshop_name || '-' },
      { label: t('headingCustomer'), value: analysis.organization_name || '-' },
    ];
    if (isPartsVendor && showPrices) {
      fields.push({ label: t('headingChainParts'), value: formatPrice(analysis.chain_parts_price) });
      fields.push({ label: t('headingOtherParts'), value: formatPrice(analysis.other_parts_price) });
    }
    if (showPrices) {
      fields.push({ label: t('headingTotalParts'), value: formatPrice(analysis.total_parts_price) });
      fields.push({ label: t('headingTasksPrice'), value: formatPrice(analysis.total_tasks_price) });
    }
    if (!isPartsVendor) {
      fields.push({ label: t('headingTasks'), value: analysis.total_tasks_count != null ? String(analysis.total_tasks_count) : '-' });
      fields.push({
        label: t('headingBilledHours'),
        value: analysis.total_billed_hours != null ? Number(analysis.total_billed_hours).toFixed(1) + ' h' : '-',
      });
    }
    if (isPartsVendor) {
      fields.push({
        label: t('headingChainPartsPercentage'),
        value: analysis.chain_parts_percentage != null ? Number(analysis.chain_parts_percentage).toFixed(1) + ' %' : '-',
      });
    }
    var approvalText = '-';
    var labelSets = this.getApprovalLabelSets();
    var approvalParts = (analysis.approval_requests || []).map(function (ar) {
      var sentAt = ar.created_at ? ' ' + moment(ar.created_at).format('D.M.') : '';
      return (labelSets.chain[ar.approval_status] || ar.approval_status) + sentAt;
    }).concat((analysis.customer_approval_requests || []).map(function (ar) {
      var sentAt = ar.created_at ? ' ' + moment(ar.created_at).format('D.M.') : '';
      return (labelSets.customer[ar.approval_status] || ar.approval_status) + sentAt;
    }));
    if (approvalParts.length) {
      approvalText = approvalParts.join(', ');
    }
    fields.push({ label: t('headingApprovalStatus'), value: approvalText });
    return fields;
  };

  // Per-row actions for the mobile card's three-dot menu (the card appends a
  // built-in "show all fields" item automatically).
  getCardOptions = (analysis) => {
    const { t, history } = this.props;
    return [
      {
        label: t('viewMaintenance'),
        icon: 'pi pi-arrow-right',
        command: () => {
          history.push('/vehicle/' + analysis.vehicle_id + '/maintenances/' + analysis.maintenance + '/');
        },
      },
      {
        label: t('viewMessageHistory'),
        icon: 'pi pi-envelope',
        command: () => { this.openMessageHistory(analysis); },
      },
    ];
  };

  applyOrdering = (newOrdering) => {
    this.setState({ ordering: newOrdering, page: 1 }, () => {
      this.saveListStateToStorage();
      this.refetchAnalysis();
    });
  };

  getSortableFields = () => {
    const { t } = this.props;
    var chainObj = this.getChainObj();
    var isPartsVendor = chainObj && chainObj.chain_type === 'parts_vendor';
    var showPrices = !chainObj || chainObj.show_maintenance_prices !== false;
    var fields = [
      { label: t('headingDate'), field: 'maintenance_date' },
      { label: t('headingRegNr'), field: 'registration_number' },
      { label: t('headingInvoiceNr'), field: 'workshop_invoice_number' },
      { label: t('headingWorkshop'), field: 'workshop__name' },
    ];
    if (isPartsVendor && showPrices) {
      fields.push({ label: t('headingChainParts'), field: 'chain_parts_price' });
      fields.push({ label: t('headingOtherParts'), field: 'other_parts_price' });
    }
    if (showPrices) {
      fields.push({ label: t('headingTotalParts'), field: 'total_parts_price' });
      fields.push({ label: t('headingTasksPrice'), field: 'total_tasks_price' });
    }
    if (isPartsVendor) {
      fields.push({ label: t('headingChainPartsPercentage'), field: 'chain_parts_percentage' });
    }
    return fields;
  };

  renderCard = (analysis) => {
    const { t } = this.props;
    var chainObj = this.getChainObj();
    var isPartsVendor = chainObj && chainObj.chain_type === 'parts_vendor';
    var showPrices = !chainObj || chainObj.show_maintenance_prices !== false;
    var pctColor = getPercentageColor(analysis.chain_parts_percentage != null ? Number(analysis.chain_parts_percentage) : 0);

    var title = (
      <Link
        to={'/vehicle/' + analysis.vehicle_id + '/'}
        style={{ color: '#80D8FF', textDecoration: 'none' }}
      >
        {analysis.registration_number || '-'}
      </Link>
    );
    var subtitle = [analysis.manufacturer, analysis.model_name].filter(Boolean).join(' ');

    var badges = [];
    if ((analysis.approval_requests && analysis.approval_requests.length)
        || (analysis.customer_approval_requests && analysis.customer_approval_requests.length)) {
      badges.push(this.getApprovalBadge(analysis));
    }

    return (
      <CBMobileCard
        key={analysis.id}
        title={title}
        dialogTitle={analysis.registration_number || '-'}
        subtitle={subtitle}
        badges={badges}
        fields={this.getCardFaceFields(analysis, isPartsVendor, showPrices, pctColor)}
        allFields={this.getCardAllFields(analysis, isPartsVendor, showPrices)}
        options={this.getCardOptions(analysis)}
        expandLabel={t('cardPartsAndTasks')}
        expandableContent={this.renderExpandedParts(analysis.maintenance, true)}
        onExpand={() => this.ensureDetailsLoaded(analysis)}
      />
    );
  };

  renderMobileList = (screenSize) => {
    const { t, chainPartsAnalysis, chainPartsAnalysisStatus, chainPartsAnalysisPagination } = this.props;

    if (chainPartsAnalysisStatus && chainPartsAnalysisStatus.loading) {
      return (
        <div style={getStyle(screenSize, styles, 'loadingContainer')}>
          <p style={getStyle(screenSize, styles, 'loadingText')}>{t('loading', { ns: 'common' })}</p>
        </div>
      );
    }

    return (
      <div style={getStyle(screenSize, styles, 'mobileListContainer')}>
        <CBMobileSortSelect
          fields={this.getSortableFields()}
          ordering={this.state.ordering}
          onChange={this.applyOrdering}
        />
        {chainPartsAnalysis && chainPartsAnalysis.length > 0 ? (
          chainPartsAnalysis.map((analysis) => this.renderCard(analysis))
        ) : (
          <div style={getStyle(screenSize, styles, 'mobileEmpty')}>
            {t('noPartsAnalysisFound')}
          </div>
        )}
        {chainPartsAnalysisPagination && chainPartsAnalysisPagination.count > 0 && (
          <div style={getStyle(screenSize, styles, 'paginatorContainer')}>
            <Paginator
              first={(chainPartsAnalysisPagination.page_size * (chainPartsAnalysisPagination.current_page - 1))}
              rows={chainPartsAnalysisPagination.page_size}
              totalRecords={chainPartsAnalysisPagination.count}
              onPageChange={this.changePage}
            />
          </div>
        )}
      </div>
    );
  };

  render() {
    const {
      t,
      chain,
      chainPartsAnalysis,
      chainPartsAnalysisStatus,
      chainPartsAnalysisPagination,
      chainPartsAnalysisSummary: summary,
    } = this.props;
    const { filters, expandedRowId, fields, selectedFields } = this.state;
    var colSpanCount = this.getVisibleColumnCount();
    var chainObj = this.getChainObj();
    var isPartsVendor = chainObj && chainObj.chain_type === 'parts_vendor';
    var showPrices = !chainObj || chainObj.show_maintenance_prices !== false;

    return (
      <CBMediaQuery>
        {screenSize => (
          <div style={getStyle(screenSize, styles, 'container')}>
            {/* Summary section */}
            {summary && screenSize === 'small' && (
              <CBStatRow items={this.getSummaryItems(summary, isPartsVendor, showPrices)} />
            )}
            {summary && showPrices && screenSize !== 'small' && (
              <div style={getStyle(screenSize, styles, 'summaryContainer')}>
                <div style={getStyle(screenSize, styles, 'summaryItem')}>
                  <p className='font-titillium-web-regular' style={getStyle(screenSize, styles, 'summaryLabel')}>
                    {t('summaryTotalMaintenances')}
                  </p>
                  <p className='font-titillium-web-bold' style={getStyle(screenSize, styles, 'summaryValue')}>
                    {summary.total_count || 0}
                  </p>
                </div>
                {isPartsVendor && (
                  <div style={getStyle(screenSize, styles, 'summaryItem')}>
                    <p className='font-titillium-web-regular' style={getStyle(screenSize, styles, 'summaryLabel')}>
                      {t('summaryChainPartsPrice')}
                    </p>
                    <p className='font-titillium-web-bold' style={getStyle(screenSize, styles, 'summaryValue')}>
                      {summary.chain_parts_price_total != null
                        ? formatPrice(summary.chain_parts_price_total)
                        : '-'}
                    </p>
                  </div>
                )}
                {isPartsVendor && (
                  <div style={getStyle(screenSize, styles, 'summaryItem')}>
                    <p className='font-titillium-web-regular' style={getStyle(screenSize, styles, 'summaryLabel')}>
                      {t('summaryOtherPartsPrice')}
                    </p>
                    <p className='font-titillium-web-bold' style={getStyle(screenSize, styles, 'summaryValue')}>
                      {summary.other_parts_price_total != null
                        ? formatPrice(summary.other_parts_price_total)
                        : '-'}
                    </p>
                  </div>
                )}
                <div style={getStyle(screenSize, styles, 'summaryItem')}>
                  <p className='font-titillium-web-regular' style={getStyle(screenSize, styles, 'summaryLabel')}>
                    {t('summaryTotalPartsPrice')}
                  </p>
                  <p className='font-titillium-web-bold' style={getStyle(screenSize, styles, 'summaryValue')}>
                    {summary.total_parts_price_total != null
                      ? formatPrice(summary.total_parts_price_total)
                      : '-'}
                  </p>
                </div>
                <div style={getStyle(screenSize, styles, 'summaryItem')}>
                  <p className='font-titillium-web-regular' style={getStyle(screenSize, styles, 'summaryLabel')}>
                    {t('summaryTotalTasksPrice')}
                  </p>
                  <p className='font-titillium-web-bold' style={getStyle(screenSize, styles, 'summaryValue')}>
                    {summary.total_tasks_price_total != null
                      ? formatPrice(summary.total_tasks_price_total)
                      : '-'}
                  </p>
                </div>
                {isPartsVendor && (
                  <div style={getStyle(screenSize, styles, 'summaryItem')}>
                    <p className='font-titillium-web-regular' style={getStyle(screenSize, styles, 'summaryLabel')}>
                      {t('summaryAveragePercentage')}
                    </p>
                    <p className='font-titillium-web-bold' style={{
                      ...getStyle(screenSize, styles, 'summaryValue'),
                      color: getPercentageColor(summary.average_chain_percentage || 0),
                    }}>
                      {summary.average_chain_percentage != null
                        ? summary.average_chain_percentage.toFixed(1) + ' %'
                        : '-'}
                    </p>
                  </div>
                )}
                {!isPartsVendor && summary.total_tasks_count != null && (
                  <div style={getStyle(screenSize, styles, 'summaryItem')}>
                    <p className='font-titillium-web-regular' style={getStyle(screenSize, styles, 'summaryLabel')}>
                      {t('summaryTotalBilledHours')}
                    </p>
                    <p className='font-titillium-web-bold' style={getStyle(screenSize, styles, 'summaryValue')}>
                      {summary.total_billed_hours != null
                        ? Number(summary.total_billed_hours).toFixed(1) + ' h'
                        : '-'}
                    </p>
                  </div>
                )}
              </div>
            )}
            {summary && !showPrices && screenSize !== 'small' && (
              <div style={getStyle(screenSize, styles, 'summaryContainer')}>
                <div style={getStyle(screenSize, styles, 'summaryItem')}>
                  <p className='font-titillium-web-regular' style={getStyle(screenSize, styles, 'summaryLabel')}>
                    {t('summaryTotalMaintenances')}
                  </p>
                  <p className='font-titillium-web-bold' style={getStyle(screenSize, styles, 'summaryValue')}>
                    {summary.total_count || 0}
                  </p>
                </div>
              </div>
            )}

            {/* Filters */}
            <div style={getStyle(screenSize, styles, 'topBarsContainer')}>
              <div style={getStyle(screenSize, styles, 'filterContainer')}>
                <div style={getStyle(screenSize, styles, 'filterRow')}>
                  <div className='datepicker-dark' style={getStyle(screenSize, styles, 'datepickerWrapper')}>
                    <span style={styles.default.datepickerLabel}>{t('filterDateFrom')}</span>
                    <CBDatePickerInput
                      displayedDate={filters.date_from ? moment(filters.date_from) : null}
                      type='date'
                      dateFormat={finnishDateFormat}
                      changeDate={(value) => {
                        const newFilters = { ...filters };
                        if (value) {
                          newFilters.date_from = moment(value).format('YYYY-MM-DD');
                        } else {
                          delete newFilters.date_from;
                        }
                        this.setState({ filters: newFilters, page: 1 }, () => {
                          this.saveListStateToStorage();
                          this.refetchAnalysis();
                        });
                      }}
                    />
                  </div>

                  <div className='datepicker-dark' style={getStyle(screenSize, styles, 'datepickerWrapper')}>
                    <span style={styles.default.datepickerLabel}>{t('filterDateTo')}</span>
                    <CBDatePickerInput
                      displayedDate={filters.date_to ? moment(filters.date_to) : null}
                      type='date'
                      dateFormat={finnishDateFormat}
                      changeDate={(value) => {
                        const newFilters = { ...filters };
                        if (value) {
                          newFilters.date_to = moment(value).format('YYYY-MM-DD');
                        } else {
                          delete newFilters.date_to;
                        }
                        this.setState({ filters: newFilters, page: 1 }, () => {
                          this.saveListStateToStorage();
                          this.refetchAnalysis();
                        });
                      }}
                    />
                  </div>

                  <CBSelectFilters
                    name='workshop_id'
                    options={this.getWorkshopOptions()}
                    onChange={(changedFilters) => {
                      const newFilters = { ...filters };
                      if (changedFilters.workshop_id) {
                        newFilters.workshop_id = changedFilters.workshop_id;
                      } else {
                        delete newFilters.workshop_id;
                      }
                      this.setState({ filters: newFilters, page: 1 }, () => {
                        this.saveListStateToStorage();
                        this.refetchAnalysis();
                      });
                    }}
                    value={filters.workshop_id || ''}
                    selectStyle={getStyle(screenSize, styles, 'selectFilter')}
                  />

                  <CBSelectFilters
                    name='organization_id'
                    options={this.getOrganizationOptions()}
                    onChange={(changedFilters) => {
                      const newFilters = { ...filters };
                      if (changedFilters.organization_id) {
                        newFilters.organization_id = changedFilters.organization_id;
                      } else {
                        delete newFilters.organization_id;
                      }
                      this.setState({ filters: newFilters, page: 1 }, () => {
                        this.saveListStateToStorage();
                        this.refetchAnalysis();
                      });
                    }}
                    value={filters.organization_id || ''}
                    selectStyle={getStyle(screenSize, styles, 'selectFilter')}
                  />

                  {isPartsVendor && (
                    <CBSelectFilters
                      name='percentage_range'
                      options={this.getPercentageOptions()}
                      onChange={this.handlePercentageFilter}
                      value={filters.percentage_range || ''}
                      selectStyle={getStyle(screenSize, styles, 'selectFilter')}
                    />
                  )}

                  <input
                    type="text"
                    placeholder={t('filterInvoiceNumber')}
                    value={filters.invoice_number || ''}
                    onChange={(e) => {
                      const newFilters = { ...filters };
                      if (e.target.value) {
                        newFilters.invoice_number = e.target.value;
                      } else {
                        delete newFilters.invoice_number;
                      }
                      this.setState({ filters: newFilters, page: 1 }, () => {
                        this.saveListStateToStorage();
                        this.refetchAnalysis();
                      });
                    }}
                    style={styles.default.searchInput}
                  />

                  <button
                    onClick={this.downloadReport}
                    style={styles.default.downloadButton}
                    className='font-titillium-web-regular'
                  >
                    {t('downloadPdfReport')}
                  </button>
                </div>
              </div>
            </div>

            {/* Table (desktop) / Cards (mobile) */}
            {screenSize === 'small' ? (
              this.renderMobileList(screenSize)
            ) : (
            <React.Fragment>
            <div
              ref={this.topScrollRef}
              onScroll={this.handleTopScroll}
              style={getStyle(screenSize, styles, 'topScrollbar')}
              className="vehicle-table-scrollbar"
            >
              <div style={getStyle(screenSize, styles, 'topScrollbarInner')} />
            </div>
            <div
              ref={this.tableScrollRef}
              onScroll={this.handleTableScroll}
              style={getStyle(screenSize, styles, 'tableContainer')}
              className="vehicle-table-scrollbar"
            >
              {chainPartsAnalysisStatus && chainPartsAnalysisStatus.loading ? (
                <div style={getStyle(screenSize, styles, 'loadingContainer')}>
                  <p style={getStyle(screenSize, styles, 'loadingText')}>Ladataan...</p>
                </div>
              ) : (
                <div>
                  <table style={getStyle(screenSize, styles, 'table')}>
                    <thead>
                      <tr>
                        <VehicleListTh
                          text={t('headingDate')}
                          fieldName='maintenance_date'
                          orderingSymbol={this.getOrderingSymbol('maintenance_date')}
                          changeOrdering={() => this.changeOrdering('maintenance_date')}
                        />
                        <VehicleListTh
                          text={t('headingRegNr')}
                          fieldName='registration_number'
                          orderingSymbol={this.getOrderingSymbol('registration_number')}
                          changeOrdering={() => this.changeOrdering('registration_number')}
                        />
                        {selectedFields.includes('invoice_number') && (
                          <VehicleListTh
                            text={t('headingInvoiceNr')}
                            fieldName='workshop_invoice_number'
                            orderingSymbol={this.getOrderingSymbol('workshop_invoice_number')}
                            changeOrdering={() => this.changeOrdering('workshop_invoice_number')}
                          />
                        )}
                        {selectedFields.includes('mileage') && (
                          <VehicleListTh
                            text={t('headingMileage')}
                            fieldName='mileage'
                            notSortable
                          />
                        )}
                        {selectedFields.includes('workshop') && (
                          <VehicleListTh
                            text={t('headingWorkshop')}
                            fieldName='workshop__name'
                            orderingSymbol={this.getOrderingSymbol('workshop__name')}
                            changeOrdering={() => this.changeOrdering('workshop__name')}
                          />
                        )}
                        {selectedFields.includes('customer') && (
                          <VehicleListTh
                            text={t('headingCustomer')}
                            fieldName='organization'
                            notSortable
                          />
                        )}
                        {selectedFields.includes('chain_parts_price') && (
                          <VehicleListTh
                            text={t('headingChainParts')}
                            fieldName='chain_parts_price'
                            orderingSymbol={this.getOrderingSymbol('chain_parts_price')}
                            changeOrdering={() => this.changeOrdering('chain_parts_price')}
                          />
                        )}
                        {selectedFields.includes('other_parts_price') && (
                          <VehicleListTh
                            text={t('headingOtherParts')}
                            fieldName='other_parts_price'
                            orderingSymbol={this.getOrderingSymbol('other_parts_price')}
                            changeOrdering={() => this.changeOrdering('other_parts_price')}
                          />
                        )}
                        {selectedFields.includes('total_parts_price') && (
                          <VehicleListTh
                            text={t('headingTotalParts')}
                            fieldName='total_parts_price'
                            orderingSymbol={this.getOrderingSymbol('total_parts_price')}
                            changeOrdering={() => this.changeOrdering('total_parts_price')}
                          />
                        )}
                        {selectedFields.includes('total_tasks_price') && (
                          <VehicleListTh
                            text={t('headingTasksPrice')}
                            fieldName='total_tasks_price'
                            orderingSymbol={this.getOrderingSymbol('total_tasks_price')}
                            changeOrdering={() => this.changeOrdering('total_tasks_price')}
                          />
                        )}
                        {selectedFields.includes('total_tasks_count') && (
                          <VehicleListTh
                            text={t('headingTasks')}
                            fieldName='total_tasks_count'
                            notSortable
                          />
                        )}
                        {selectedFields.includes('total_billed_hours') && (
                          <VehicleListTh
                            text={t('headingBilledHours')}
                            fieldName='total_billed_hours'
                            notSortable
                          />
                        )}
                        {selectedFields.includes('approval_status') && (
                          <VehicleListTh
                            text={t('headingApprovalStatus')}
                            fieldName='approval_status'
                            notSortable
                          />
                        )}
                        {isPartsVendor && (
                          <VehicleListTh
                            text={t('headingChainPartsPercentage')}
                            fieldName='chain_parts_percentage'
                            orderingSymbol={this.getOrderingSymbol('chain_parts_percentage')}
                            changeOrdering={() => this.changeOrdering('chain_parts_percentage')}
                          />
                        )}
                        <th style={getStyle(screenSize, styles, 'tableHeaderMessage')} />
                        <th style={getStyle(screenSize, styles, 'tableHeaderSettings')}>
                          <MultiSelect
                            value={selectedFields}
                            onChange={(e) => this.setSelectedFields(e.value)}
                            options={fields}
                            className="bars"
                            dropdownIcon={undefined}
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {chainPartsAnalysis && chainPartsAnalysis.length > 0 ? (
                        chainPartsAnalysis.map((analysis) => {
                          const pctValue = analysis.chain_parts_percentage != null ? Number(analysis.chain_parts_percentage) : 0;
                          const pctColor = getPercentageColor(pctValue);
                          const isExpanded = expandedRowId === analysis.id;
                          return (
                            <React.Fragment key={analysis.id}>
                              <tr
                                style={{
                                  ...getStyle(screenSize, styles, 'tableRow'),
                                  backgroundColor: isExpanded ? 'rgba(0, 149, 218, 0.12)' : undefined,
                                }}
                                onClick={() => this.toggleRow(analysis)}
                              >
                                <VehicleListTd
                                  textPrimary={
                                    <span>
                                      <img
                                        src={isExpanded ? '/img/up_arrow_icon.svg' : '/img/down_arrow_icon.svg'}
                                        alt=''
                                        style={{ width: 14, height: 14, marginRight: 8, opacity: 0.5, verticalAlign: 'middle' }}
                                      />
                                      {analysis.maintenance_date
                                        ? moment(analysis.maintenance_date).format('DD.MM.YYYY')
                                        : '-'}
                                    </span>
                                  }
                                />
                                <VehicleListTd
                                  textPrimary={
                                    <Link
                                      to={'/vehicle/' + analysis.vehicle_id + '/'}
                                      onClick={function (e) { e.stopPropagation(); }}
                                      style={{ color: '#80D8FF', textDecoration: 'none' }}
                                    >
                                      {analysis.registration_number || '-'}
                                    </Link>
                                  }
                                  textSecondary={[analysis.manufacturer, analysis.model_name].filter(Boolean).join(' ')}
                                  additionalStylesPrimaryText={{ fontSize: 24 }}
                                />
                                {selectedFields.includes('invoice_number') && (
                                  <VehicleListTd
                                    textPrimary={analysis.workshop_invoice_number || '-'}
                                  />
                                )}
                                {selectedFields.includes('mileage') && (
                                  <VehicleListTd
                                    textPrimary={analysis.mileage
                                      ? Number(analysis.mileage).toLocaleString('fi-FI') + ' km'
                                      : '-'}
                                  />
                                )}
                                {selectedFields.includes('workshop') && (
                                  <VehicleListTd
                                    textPrimary={analysis.workshop_name || '-'}
                                  />
                                )}
                                {selectedFields.includes('customer') && (
                                  <VehicleListTd
                                    textPrimary={analysis.organization_name || '-'}
                                  />
                                )}
                                {selectedFields.includes('chain_parts_price') && (
                                  <VehicleListTd
                                    textPrimary={formatPrice(analysis.chain_parts_price)}
                                  />
                                )}
                                {selectedFields.includes('other_parts_price') && (
                                  <VehicleListTd
                                    textPrimary={formatPrice(analysis.other_parts_price)}
                                  />
                                )}
                                {selectedFields.includes('total_parts_price') && (
                                  <VehicleListTd
                                    textPrimary={formatPrice(analysis.total_parts_price)}
                                  />
                                )}
                                {selectedFields.includes('total_tasks_price') && (
                                  <VehicleListTd
                                    textPrimary={formatPrice(analysis.total_tasks_price)}
                                  />
                                )}
                                {selectedFields.includes('total_tasks_count') && (
                                  <VehicleListTd
                                    textPrimary={analysis.total_tasks_count != null
                                      ? analysis.total_tasks_count
                                      : '-'}
                                  />
                                )}
                                {selectedFields.includes('total_billed_hours') && (
                                  <VehicleListTd
                                    textPrimary={analysis.total_billed_hours != null
                                      ? Number(analysis.total_billed_hours).toFixed(1) + ' h'
                                      : '-'}
                                  />
                                )}
                                {selectedFields.includes('approval_status') && (
                                  <VehicleListTd
                                    textPrimary={this.getApprovalBadge(analysis)}
                                  />
                                )}
                                {isPartsVendor && (
                                  <VehicleListTd
                                    textPrimary={analysis.chain_parts_percentage != null
                                      ? Number(analysis.chain_parts_percentage).toFixed(1) + ' %'
                                      : '-'}
                                    additionalStylesPrimaryText={{
                                      color: pctColor,
                                      fontWeight: 'bold',
                                      fontSize: 18,
                                    }}
                                  />
                                )}
                                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                  {this.renderMessageHistoryButton(analysis)}
                                </td>
                                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                  <Link
                                    to={'/vehicle/' + analysis.vehicle_id + '/maintenances/' + analysis.maintenance + '/'}
                                    onClick={function (e) { e.stopPropagation(); }}
                                    style={styles.default.maintenanceLinkButton}
                                    title={t('viewMaintenance')}
                                  >
                                    <img
                                      src='/img/icon_arrow_right_2.svg'
                                      alt=''
                                      style={{ width: 18, height: 18, opacity: 0.7, verticalAlign: 'middle' }}
                                    />
                                  </Link>
                                </td>
                              </tr>
                              {isExpanded && (
                                <tr>
                                  <td colSpan={colSpanCount} style={expandedStyles.cell}>
                                    {this.renderExpandedParts(analysis.maintenance)}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={colSpanCount} style={getStyle(screenSize, styles, 'noDataCell')}>
                            {t('noPartsAnalysisFound')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {chainPartsAnalysisPagination && chainPartsAnalysisPagination.count > 0 && (
                    <div style={getStyle(screenSize, styles, 'paginatorContainer')}>
                      <Paginator
                        first={(chainPartsAnalysisPagination.page_size * (chainPartsAnalysisPagination.current_page - 1))}
                        rows={chainPartsAnalysisPagination.page_size}
                        totalRecords={chainPartsAnalysisPagination.count}
                        onPageChange={this.changePage}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            </React.Fragment>
            )}
            {this.state.historyModalOpen && (
              <VehicleMessageHistoryModal
                modalIsOpen={this.state.historyModalOpen}
                closeModal={this.closeMessageHistory}
                registrationNumbers={this.state.historyReg ? [this.state.historyReg] : []}
                referenceDate={this.state.historyDate}
                chainId={this.props.selectedChainId}
                title={(this.state.historyReg || '')
                  + (this.state.historyDate ? (' · ' + moment(this.state.historyDate).format('DD.MM.YYYY')) : '')}
              />
            )}
          </div>
        )}
      </CBMediaQuery>
    );
  }
}

const styles = {
  default: {
    container: {
      width: '100%',
      padding: '0px 16px 0px 0px',
    },
    mobileListContainer: {
      padding: '0 16px 24px 16px',
    },
    mobileEmpty: {
      color: 'rgba(255, 255, 255, 0.7)',
      fontSize: 15,
      padding: '24px 16px',
      textAlign: 'center',
    },
    summaryContainer: {
      display: 'flex',
      flexDirection: 'row',
      gap: 40,
      padding: '24px 16px 20px 16px',
    },
    summaryItem: {
      display: 'flex',
      flexDirection: 'column',
    },
    summaryLabel: {
      color: 'rgba(255, 255, 255, 0.7)',
      fontSize: 12,
      margin: 0,
    },
    summaryValue: {
      color: '#FFFFFF',
      fontSize: 24,
      margin: 0,
    },
    topBarsContainer: {
      paddingLeft: 16,
      marginBottom: 16,
    },
    filterContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
    },
    filterRow: {
      display: 'flex',
      flexDirection: 'row',
      gap: 12,
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    datepickerWrapper: {
      width: 200,
      flexShrink: 0,
    },
    datepickerLabel: {
      color: 'rgba(255, 255, 255, 0.6)',
      fontSize: 13,
      marginRight: 6,
      whiteSpace: 'nowrap',
    },
    selectFilter: {
      width: 200,
      marginLeft: 0,
      marginRight: 0,
    },
    searchInput: {
      backgroundColor: 'rgba(0, 0, 0, 0.15)',
      border: '1px solid rgba(255, 255, 255, 0.25)',
      borderRadius: 4,
      color: '#fff',
      padding: '6px 12px',
      fontSize: 14,
      width: 200,
    },
    tableContainer: {
      width: '100%',
      overflowX: 'auto',
      marginBottom: '8px',
      paddingBottom: 16,
      position: 'relative',
    },
    table: {
      whiteSpace: 'nowrap',
      borderCollapse: 'separate',
      borderSpacing: 0,
    },
    topScrollbar: {
      overflowX: 'auto',
      overflowY: 'hidden',
      width: '100%',
      marginTop: 12,
    },
    topScrollbarInner: {
      height: 1,
    },
    tableRow: {
      height: '60px',
      width: '100%',
      cursor: 'pointer',
    },
    tableHeaderSettings: {
      cursor: 'pointer',
      WebkitUserSelect: 'none',
      MsUserSelect: 'none',
      UserSelect: 'none',
      padding: '8px 8px 8px 8px',
    },
    tableHeaderMessage: {
      padding: 8,
      width: 72,
    },
    messageHistoryButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 52,
      height: 32,
      padding: '0 10px',
      borderRadius: 16,
      border: '1px solid rgba(255, 255, 255, 0.3)',
      cursor: 'pointer',
    },
    messageHistoryButtonEmpty: {
      border: '1px solid rgba(255, 255, 255, 0.1)',
    },
    messageHistoryCount: {
      marginLeft: 6,
      fontSize: 12,
      fontFamily: 'TitilliumWeb-Bold',
      color: '#FFFFFF',
    },
    noDataCell: {
      padding: '24px 16px',
      color: 'rgba(255, 255, 255, 0.7)',
      fontSize: 14,
      textAlign: 'center',
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: 200,
    },
    loadingText: {
      color: 'rgba(255, 255, 255, 0.7)',
      fontSize: 14,
    },
    paginatorContainer: {
      display: 'flex',
      justifyContent: 'center',
      paddingBottom: 24,
    },
    maintenanceLinkButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      borderRadius: 16,
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    downloadButton: {
      backgroundColor: 'rgba(0, 149, 218, 0.8)',
      color: '#fff',
      border: 'none',
      borderRadius: 4,
      padding: '8px 16px',
      cursor: 'pointer',
      fontSize: 13,
      whiteSpace: 'nowrap',
      flexShrink: 0,
    },
  },
  medium: {
    datepickerWrapper: {
      width: 190,
      flexShrink: 0,
    },
    selectFilter: {
      width: 180,
      marginLeft: 0,
      marginRight: 0,
    },
  },
  small: {
    // The desktop container adds right-only padding (0px 16px 0px 0px); on mobile
    // zero it and let each child own a symmetric 16px so cards/stats aren't inset
    // 32px on the right while the left sits at 16px.
    container: {
      padding: 0,
    },
    topBarsContainer: {
      paddingLeft: 16,
      paddingRight: 16,
    },
    filterRow: {
      gap: 8,
    },
    datepickerWrapper: {
      width: 'calc(50% - 4px)',
      flexShrink: 0,
    },
    selectFilter: {
      width: '100%',
      marginLeft: 0,
      marginRight: 0,
    },
  },
};

var expandedStyles = {
  cell: {
    padding: 0,
    backgroundColor: 'rgba(0, 149, 218, 0.06)',
    borderBottom: '2px solid rgba(0, 149, 218, 0.3)',
  },
  content: {
    display: 'flex',
    flexDirection: 'row',
    gap: 32,
    padding: '16px 24px',
    whiteSpace: 'normal',
    maxWidth: 'calc(100vw - 48px)',
  },
  section: {
    flex: 1,
    minWidth: 0,
  },
  sectionTitle: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 13,
    fontWeight: 600,
    textTransform: 'uppercase',
    marginBottom: 8,
    marginTop: 0,
    letterSpacing: 0.5,
  },
  subBlock: {
    marginTop: 16,
  },
  noParts: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: 14,
    padding: '4px 0',
    margin: 0,
  },
  loading: {
    padding: '16px 24px',
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 14,
  },
};

var partRowStyles = {
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
    borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
  },
  leftSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
    gap: 8,
  },
  rightSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexShrink: 0,
  },
  chainBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: '#4CAF50',
    color: '#FFF',
    fontSize: 11,
    fontWeight: 'bold',
    flexShrink: 0,
  },
  otherBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: '#F44336',
    color: '#FFF',
    fontSize: 11,
    fontWeight: 'bold',
    flexShrink: 0,
  },
  name: {
    color: '#FFF',
    fontSize: 14,
    fontFamily: 'TitilliumWeb-SemiBold',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  amount: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 13,
    flexShrink: 0,
  },
  typeCode: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: 12,
    fontFamily: 'monospace',
  },
  price: {
    color: '#FFF',
    fontSize: 14,
    fontFamily: 'TitilliumWeb-SemiBold',
    minWidth: 70,
    textAlign: 'right',
  },
};

function mapStateToProps(state) {
  return {
    user: state.authUser.user,
    selectedChainId: state.chain.selectedChainId,
    chainMemberships: state.chain.chainMemberships,
    chainCareServices: state.chain.chainCareServices,
    chainPartsAnalysis: state.chain.chainPartsAnalysis,
    chainPartsAnalysisPagination: state.chain.chainPartsAnalysisPagination,
    chainPartsAnalysisStatus: state.chain.chainPartsAnalysisStatus,
    chainPartsAnalysisSummary: state.chain.chainPartsAnalysisSummary,
    chainMaintenanceDetails: state.chain.chainMaintenanceDetails,
  };
}

export default connect(
  mapStateToProps,
  {
    fetchChainPartsAnalysis: _fetchChainPartsAnalysis,
    fetchChainPartsAnalysisSummary: _fetchChainPartsAnalysisSummary,
    fetchChainCareServices: _fetchChainCareServices,
    fetchChainMemberships: _fetchChainMemberships,
    fetchChainMaintenanceDetails: _fetchChainMaintenanceDetails,
  },
)(withRouter(translate('ChainHomeView')(ChainPartsUsageView)));
